/** @prettier */

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Box,
} from '@mui/material';
import {useState} from 'react';
import {useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';

//MTH component import
import APIErrorMessage from '../../component/APIErrorMessage';
import {MultiGroupSelectBox} from './GroupSelectBox';
import {PrimaryButton, SecondaryButton} from '../../component/BasicButton';
//API import
import {putTodaySchedule} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';

import {apiErrorAlert} from '../../functions/apiErrorAlert';
import DialogPage from '../../component/DialogMessage';

/**
 * 当日スケジュールを他グループの当日スケジュールへコピーするダイアログ
 * @param props props
 * @returns JSX
 */
const CopyToDialog = (props: {
  /** ダイアログ表示フラグ */
  isOpenDialog: boolean;
  /** ダイアログ表示フラグ 設定関数 */
  setIsOpenDialog: React.Dispatch<React.SetStateAction<boolean>>;
  /** スケジュール更新関数 */
  scheduleChangeHandler: () => void;
  /** 当日スケジュールBodyデータ */
  todayScheduleData: apiIF.res.getTodaySchedule['today-schedule'];
  /** コピーされるグループのID */
  groupID: string;
  /** グループ情報一覧 */
  groupInfo: apiIF.res.getCompositionsGroups['group-informations'];
}) => {
  const trans = useTranslation().t; //翻訳関数

  //パスパラメータ取得
  const {siteID} = useParams();
  /** セレクトボックスで選択したグループID */
  const [targetGroupID, setTargetGroupID] = useState<string[]>([]);

  //フラグ
  /** 確認ダイアログのオープンフラグ */
  const [isOpenDialog, setIsOpenDialog] = useState(false);
  /** API応答待ちフラグ */
  const [apiWait, setAPIWait] = useState(false);
  /** APIエラー時エラーメッセージ表示 */
  const [isOpenErrorDialog, setIsOpenErrorDialog] = useState(false);

  /** コピーされるグループの情報 */
  const copiedGroupInfo = props.groupInfo.find(info => {
    return info['group-id'] === props.groupID;
  });

  /**
   * セレクトボックスで選択したグループの当日スケジュールに
   * 元のグループの当日スケジュールをコピーするためのBodyデータを作成
   * @param  bodyData 引数のBodyデータ
   * @returns リクエストBodyのデータ
   */
  const copyToScheduleData = (
    bodyData: apiIF.res.getTodaySchedule['today-schedule']
  ) => {
    /** Bodyデータの初期値 */
    let copyData: apiIF.reqParameters.putTodaySchedule = {
      'today-schedule': [],
      'copy-destination': [],
    };
    //引数Bodyデータのスケジュールデータをcopyデータに格納する
    copyData['today-schedule'] = bodyData;
    //セレクトボックスで選択されたグループのIDを'copy-destination'に加える
    targetGroupID.forEach(value => {
      copyData['copy-destination']?.push(value);
    });
    return copyData;
  };

  /**
   * COPYボタン押下時処理
   */
  const handleClickCopy = async () => {
    setAPIWait(true);
    if (siteID === undefined) return;
    //コピー先を指定したBodyデータを作成
    const copyScheduleDataBody = copyToScheduleData(props.todayScheduleData);
    try {
      await putTodaySchedule(siteID, props.groupID, copyScheduleDataBody);
      //スケジュール設定更新
      props.scheduleChangeHandler();
      handleClose();
    } catch (error) {
      apiErrorAlert(trans);
    } finally {
      setAPIWait(false);
      setIsOpenDialog(false);
    }
  };

  /**
   * CANCELボタン押下時処理
   */
  const handleClose = () => {
    setTargetGroupID([]);
    props.setIsOpenDialog(false);
  };

  return (
    <>
      <Dialog
        open={props.isOpenDialog}
        onClose={handleClose}
        aria-labelledby="form-dialog-title"
        className="schedule__copyDialog"
        maxWidth="md">
        <DialogTitle id="form-dialog-title">
          {trans('CopyToDialog.title')}
        </DialogTitle>
        <DialogContent className="schedule__copyDialog__content">
          <Stack spacing={2}>
            {/** グループの名前 */}
            <Box>
              {copiedGroupInfo === undefined
                ? ''
                : copiedGroupInfo['group-name']}
            </Box>
            {/** グループの選択欄 */}
            <Box className="schedule__copyDialog__content__groupSelectArea">
              <Grid container spacing={4} alignItems="center">
                <Grid item xs={2}>
                  {trans('CopyToDialog.to')}
                </Grid>
                <Grid item xs={10}>
                  <MultiGroupSelectBox
                    selectedGroupID={targetGroupID}
                    setSelectedGroupID={setTargetGroupID}
                    groupInfo={props.groupInfo}
                    excludeGroupID={props.groupID}
                  />
                </Grid>
              </Grid>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <SecondaryButton
            onClick={handleClose}
            buttonText={trans('CopyToDialog.buttonTxt.cancel')}
          />
          <PrimaryButton
            onClick={() => setIsOpenDialog(true)}
            disabled={targetGroupID.length === 0}
            apiWait={apiWait}
            buttonText={trans('CopyToDialog.buttonTxt.copy')}
          />
        </DialogActions>
      </Dialog>
      <APIErrorMessage
        open={isOpenErrorDialog}
        setOpen={setIsOpenErrorDialog}
      />
      <DialogPage
        open={isOpenDialog}
        setOpen={setIsOpenDialog}
        titleString={trans('Confirm.dialogBox.title')}
        contentText={trans('Confirm.dialogBox.msg')}
        buttonNameLeft={trans('Confirm.dialogBox.buttonTxt.cancel')}
        buttonNameRight={trans('Confirm.dialogBox.buttonTxt.ok')}
        redirectPath={''}
        execFunc={handleClickCopy}
        id="mv-CopyToDialog"
      />
    </>
  );
};
export default CopyToDialog;
